import { Task } from "@/types/flows";

export const enum DataMode {
    IMAGE = "image",
    VIDEO = "video",
    PHONE = "phone",
    TEST_FILES = "test_files"
}

export type DataModeType = (typeof DataMode)[keyof typeof DataMode];

export const enum ImageDeployment {
    SINGLE = "single",
    MULTIPLE = "multiple",
    EDGE = "edge"
}

export type ImageDeploymentType = (typeof ImageDeployment)[keyof typeof ImageDeployment];

export const enum VideoDeployment {
    FILE = "file",
    EDGE = "edge",
    STREAM = "stream"
}

export type VideoDeploymentType = (typeof VideoDeployment)[keyof typeof VideoDeployment];

// This is data needed to build code snippets
export interface WorkflowCredentialsType {
    apiKey: string;
    workspaceUrl: string;
    workflowUrl: string;
    baseUrl: string;
    dataMode: DataModeType;
}

// Some of those types/interfaces also exist in the app/functions/rapid/schemas.ts file.
// TODO: move those to a shared file we can use in both frontend and backend.
export enum RapidTrainStatus {
    PENDING = "pending",
    TRAINING = "training",
    FINISHED = "finished",
    FAILED = "failed"
}

export type RapidModelTrainData = { startedAt?: Date; finishedAt?: Date; status: RapidTrainStatus };

export type RapidModelInfo = { id?: string; train: RapidModelTrainData };

export enum AvailableRapidModelArchitectures {
    RF_DETR = "rf_detr",
    RF_INSTANT = "rf_instant"
}

type RapidModelsSavedInfo = {
    [AvailableRapidModelArchitectures.RF_INSTANT]: RapidModelInfo[];
    [AvailableRapidModelArchitectures.RF_DETR]: RapidModelInfo[];
};

// Typing for records saved in rapid_data collection - that is the collection that links
// workflows, models, etc.
export interface RapidDataType {
    id: string;
    url: string; // Shareable slug for public access (8-char unique identifier)
    task: NonNullable<Task>;
    workspaceId: string;
    workflowId: string;
    models: RapidModelsSavedInfo;
    lastExecutedAt?: Date;
}

export interface RapidWorkflowDataType {
    id: string;
    url: string;
    name: string;
    rapidDataId: string;
    config: string;
    owner: string; // same as workspaceId
    public?: boolean; // Whether the workflow is publicly accessible
    rapidData?: RapidDataType; // Include rapidData when fetched from backend
}

export enum CodeSnippetEnv {
    PYTHON = "Python",
    CURL = "cURL",
    JAVASCRIPT = "JavaScript",
    SWIFT = "Swift",
    NET = "Net"
}

export type CodeSnippetEnvType = (typeof CodeSnippetEnv)[keyof typeof CodeSnippetEnv];

export interface CodeSnippetType {
    code: string;
    env: string;
    description?: string | React.ReactNode;
}

export interface IntegrationCopyType {
    title: string;
    icon: string;
    codeSnippets: CodeSnippetType[];
    documentationUrl?: string;
}

export interface IntegrationCopyCMSType {
    [DataMode.IMAGE]: Record<ImageDeploymentType, IntegrationCopyType>;
    [DataMode.VIDEO]: Record<VideoDeploymentType, IntegrationCopyType>;
}
